import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import useAppToast from '@/hooks/useAppToast';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { setRezeptStatus, startPdf } from '@/services/main/rezept/rezeptListService';
import type { RezeptListItem } from '@/types/rezept.types';
import { getApiErrorMessage } from '@/utils/apiError';

const ALREADY_PROCESSING_MESSAGE =
  'レセプト発行中です。\n再度レセプトを発行したい場合には、レセプト発行画面を再検索してください。';
const NO_SELECTION_MESSAGE = '対象のレセプトを選択してください。';
const DEFAULT_ERROR_MESSAGE = 'レセプト発行に失敗しました';
const POPUP_BLOCKED_MESSAGE = 'ポップアップがブロックされました。ブラウザの設定を確認してください。';

// Shown in the new tab while 状態更新 + PDF ジョブ登録 run, so it is not a blank
// white tab until the progress page boots. Mirrors 訪問データチェック.
const PDF_TAB_PLACEHOLDER_HTML =
  '<!doctype html><html><head><title>レセプト発行</title></head>' +
  '<body style="font-family:sans-serif;padding:24px;">レセプト発行中...</body></html>';

/**
 * Orchestrates the レセプト発行 flow:
 * 1. Update rezept status to 印刷済
 * 2. Start PDF generation job (queue)
 * 3. Open a new tab for progress + PDF display
 */
export function useRezeptIssue() {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const { mutateAsync: setStatusMutation } = useMutation({
    mutationFn: (params: { rezeptIds: number[]; addTreatmentReports: Record<number, boolean> }) =>
      setRezeptStatus(tenant, clinicNumber, {
        rezept_ids: params.rezeptIds,
        add_treatment_reports: params.addTreatmentReports,
      }),
    onError: error => {
      toast.error(getApiErrorMessage(error, DEFAULT_ERROR_MESSAGE));
    },
  });

  const { mutateAsync: startPdfMutation } = useMutation({
    mutationFn: (params: {
      rezeptIds: number[];
      sortRadio: number;
      addTreatmentReports: Record<number, boolean>;
    }) =>
      startPdf(tenant, clinicNumber, {
        rezept_ids: params.rezeptIds,
        sort_radio: params.sortRadio,
        add_treatment_reports: params.addTreatmentReports,
      }),
    onError: error => {
      toast.error(getApiErrorMessage(error, DEFAULT_ERROR_MESSAGE));
    },
  });

  const issueRezepts = useCallback(
    async (selectedRows: RezeptListItem[], sortRadio: number): Promise<number[] | null> => {
      if (isProcessing) {
        alert(ALREADY_PROCESSING_MESSAGE);
        return null;
      }

      if (selectedRows.length === 0) {
        alert(NO_SELECTION_MESSAGE);
        return null;
      }

      setIsProcessing(true);

      // Open the destination tab synchronously, while the click is still the current user
      // activation: after the two awaits below the browser treats window.open as unsolicited
      // and blocks it. Same pattern as 請求患者一覧 (bill_pt_pdf) / 訪問データチェック on this
      // screen and as [来院用]領収書. Plain '_blank' with no feature string — a feature string
      // is what made this the only print flow to open a chrome-less popup window instead of a
      // tab, and the named target ('rezept_pdf') let a second 発行 hijack the first one's tab.
      const pdfTab = window.open('', '_blank');
      if (pdfTab) {
        pdfTab.document.write(PDF_TAB_PLACEHOLDER_HTML);
        pdfTab.document.close();
      }

      try {
        // 確定 (status=2) rows are still printed; BE just skips updating their
        // status to 印刷済. Send all selected ids to both mutations, but return
        // only non-確定 ids so the caller's optimistic cache update doesn't flip
        // 確定 → 印刷済 (which would get reverted on next refetch).
        const rezeptIds = selectedRows.map(r => r.id);
        const addTreatmentReports: Record<number, boolean> = {};
        selectedRows.forEach(r => {
          addTreatmentReports[r.id] = r.add_treatment_report === 1;
        });
        const statusUpdatableIds = selectedRows
          .filter(r => r.status !== 2)
          .map(r => r.id);

        await setStatusMutation({ rezeptIds, addTreatmentReports });

        const { rezept_token } = await startPdfMutation({
          rezeptIds,
          sortRadio,
          addTreatmentReports,
        });

        const progressPath = `/${tenant}/rezepts/pdf-progress?token=${rezept_token}`;

        if (pdfTab && !pdfTab.closed) {
          pdfTab.location.href = progressPath;
          pdfTab.focus();
        } else if (!window.open(progressPath, '_blank')) {
          // Blocked outright, or the user closed the tab while the job was starting.
          toast.error(POPUP_BLOCKED_MESSAGE);
          setIsProcessing(false);
          return null;
        }

        setIsProcessing(false);
        return statusUpdatableIds;
      } catch {
        // Leave no orphan placeholder tab behind when 状態更新 / ジョブ登録 fails.
        pdfTab?.close();
        setIsProcessing(false);
        return null;
      }
    },
    [isProcessing, tenant, setStatusMutation, startPdfMutation]
  );

  return { issueRezepts, isProcessing };
}

export default useRezeptIssue;
